import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Upload, Sparkles, Loader2, Check, X, ArrowRight, FileText, ShieldCheck,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  compliant: { label: 'Compliant', tone: 'text-primary', ring: 'hsl(var(--primary))', badge: 'bg-primary/15 text-primary' },
  warning: { label: 'Needs attention', tone: 'text-[hsl(36,90%,50%)]', ring: 'hsl(36,90%,50%)', badge: 'bg-[hsl(36,90%,50%)]/15 text-[hsl(36,90%,50%)]' },
  non_compliant: { label: 'Non-compliant', tone: 'text-destructive', ring: 'hsl(var(--destructive))', badge: 'bg-destructive/15 text-destructive' },
  expired: { label: 'Expired', tone: 'text-destructive', ring: 'hsl(var(--destructive))', badge: 'bg-destructive/15 text-destructive' },
};

const money = (n) => (typeof n === 'number' ? `$${n.toLocaleString()}` : '—');

export const TryItDemo = () => {
  const navigate = useNavigate();
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [activeSample, setActiveSample] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    fetch(`${BACKEND}/api/public/demo/samples`)
      .then((r) => r.json())
      .then((d) => setSamples(d.samples || []))
      .catch(() => setSamples([]));
  }, []);

  const runSample = async (id) => {
    setLoading(true); setError(''); setResult(null); setActiveSample(id);
    try {
      const r = await fetch(`${BACKEND}/api/public/demo/extract-sample`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sample_id: id }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Extraction failed');
      setResult(d);
    } catch (e) {
      setError(e.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const runUpload = async (file) => {
    if (!file) return;
    setLoading(true); setError(''); setResult(null); setActiveSample(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${BACKEND}/api/public/demo/extract`, { method: 'POST', body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Extraction failed');
      setResult(d);
    } catch (e) {
      setError(e.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const ex = result?.extraction || {};
  const comp = result?.compliance || {};
  const meta = STATUS_META[comp.status] || STATUS_META.warning;
  const limits = ex.coverage_limits || {};
  const addl = (ex.additional_insured || []).filter(Boolean);

  return (
    <div data-testid="try-it-demo" className="rounded-2xl border border-border bg-card p-6 sm:p-8">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
          <Sparkles className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-semibold text-lg" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Watch the AI read a real COI</h3>
          <p className="text-sm text-muted-foreground">Pick a sample certificate or upload your own — the same engine that powers VendorSuite reads and scores it live.</p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        {samples.map((s) => (
          <Button
            key={s.id}
            variant={activeSample === s.id ? 'default' : 'outline'}
            size="sm"
            disabled={loading}
            data-testid={`try-sample-${s.id}`}
            onClick={() => runSample(s.id)}
            title={s.description}
          >
            <FileText className="mr-1.5 h-3.5 w-3.5" /> {s.label}
          </Button>
        ))}
        <span className="text-xs text-muted-foreground mx-1">or</span>
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,.tiff"
          className="hidden"
          data-testid="try-upload-input"
          onChange={(e) => runUpload(e.target.files?.[0])}
        />
        <Button variant="secondary" size="sm" disabled={loading} data-testid="try-upload-button" onClick={() => fileRef.current?.click()}>
          <Upload className="mr-1.5 h-3.5 w-3.5" /> Upload your COI
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground mb-6">PDF, PNG or JPEG up to 10MB. Documents are processed for this demo and not stored.</p>

      {/* States */}
      {loading && (
        <div data-testid="try-loading" className="flex items-center gap-3 rounded-xl border bg-background/60 p-6 text-sm text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          Reading the document and scoring it with AI… this takes a few seconds.
        </div>
      )}

      {error && !loading && (
        <div data-testid="try-error" className="rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      {result && !loading && (
        <div data-testid="try-result" className="grid gap-5 md:grid-cols-5">
          {/* Score */}
          <div className="md:col-span-2 rounded-xl border bg-background/60 p-5 flex flex-col items-center justify-center text-center">
            <div className="relative h-28 w-28">
              <svg viewBox="0 0 36 36" className="h-28 w-28 -rotate-90">
                <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="hsl(var(--muted))" strokeWidth="3" />
                <path
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none" stroke={meta.ring} strokeWidth="3" strokeLinecap="round"
                  strokeDasharray="100" strokeDashoffset={100 - (comp.score ?? 0)}
                  style={{ transition: 'stroke-dashoffset 1s cubic-bezier(0.22,1,0.36,1)' }}
                />
              </svg>
              <span data-testid="try-score" className={`absolute inset-0 flex items-center justify-center text-3xl font-bold ${meta.tone}`} style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {comp.score}
              </span>
            </div>
            <span data-testid="try-status-badge" className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${meta.badge}`}>
              <ShieldCheck className="h-3.5 w-3.5" /> {meta.label}
            </span>
            <p className="text-[11px] text-muted-foreground mt-2">Compliance score / 100</p>
          </div>

          {/* Extracted fields + checks */}
          <div className="md:col-span-3 space-y-4">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">AI-extracted fields</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <Field label="Insured" value={ex.insured_name} />
                <Field label="Carrier" value={ex.insurance_provider} />
                <Field label="Policy #" value={ex.policy_number} />
                <Field label="Expires" value={ex.expiration_date} />
                <Field label="GL / occurrence" value={money(limits.each_occurrence ?? limits.general_liability)} />
                <Field label="Additional insured" value={addl.length ? addl.join(', ') : 'None found'} />
              </div>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">Compliance checks</p>
              <ul className="space-y-1.5">
                {(comp.checks || []).map((c, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span className={`mt-0.5 flex h-4 w-4 items-center justify-center rounded-full shrink-0 ${c.passed ? 'bg-primary/15 text-primary' : 'bg-destructive/15 text-destructive'}`}>
                      {c.passed ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                    </span>
                    <span className="text-foreground">{c.label}</span>
                    <span className="text-muted-foreground text-xs ml-auto text-right">{c.detail}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {result && !loading && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t pt-5">
          <p className="text-sm text-muted-foreground">This is exactly what happens for every document your vendors submit — automatically.</p>
          <Button data-testid="try-cta" onClick={() => navigate('/signup')}>
            Do this for all your vendors <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value }) => (
  <div>
    <p className="text-[11px] text-muted-foreground">{label}</p>
    <p className="font-medium truncate" title={value || ''}>{value || '—'}</p>
  </div>
);
